import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { MAIN_URL } from "../../../Configurations/Urls";
import Layout4 from "../../DataLayouts/Layout4";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import ListSummaryCards, {
  countWhere,
} from "../../../Components/List/ListSummaryCards";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box } from "@mui/material";

export const DEFAULT_COLUMNS = [
  {
    field: "client_name",
    label: "Client Name",
    visible: true,
    width: 200,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "client_id",
    label: "Client ID",
    visible: true,
    width: 110,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "email",
    label: "Email",
    visible: true,
    width: 220,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "phone",
    label: "Phone Number",
    visible: true,
    width: 160,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "created_date",
    label: "Created Date",
    visible: true,
    width: 140,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "license_id",
    label: "License ID",
    visible: true,
    width: 140,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "organizations_count",
    label: "Organizations",
    visible: true,
    width: 130,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "users_count",
    label: "Users",
    visible: true,
    width: 100,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "employees_count",
    label: "Employees",
    visible: true,
    width: 110,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
];

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "created_at",
  sortOrder: "desc",
};

function ClientList() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);

 function formatDate(date) {
  if (!date || date === "-") return null;

  const d = new Date(date);
  // Check if date is valid
  if (isNaN(d.getTime())) return null;

  const day = String(d.getDate()).padStart(2, '0');      // 1 → "01"
  const month = String(d.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
  const year = d.getFullYear();

  return `${day}-${month}-${year}`;
}

  const normalizeName = (name) =>
    String(name ?? "")
      .replace(/\s+/g, " ")
      .replace(/\n/g, " ")
      .trim();

  const getRelatedArray = (client, keys) => {
    for (const key of keys) {
      if (Array.isArray(client?.[key])) return client[key];
    }
    return [];
  };

  const getPrimaryUser = (client) => {
    const users = getRelatedArray(client, [
      "application_user",
      "application_users",
      "users",
      "client_users",
    ]);
    const normalizedClientName = normalizeName(client?.client_name).toLowerCase();
    const matchedByName = users.find(
      (u) => normalizeName(u?.full_name).toLowerCase() === normalizedClientName
    );
    return matchedByName || users.find((u) => u?.is_active) || users[0] || null;
  };

  const formatPhone = (user) => {
    const code = user?.country_phone_code
      ? String(user.country_phone_code).trim()
      : "";
    const phone = user?.phone_number ? String(user.phone_number).trim() : "";
    if (!code && !phone) return "-";
    return `${code}${code && phone ? " " : ""}${phone}`;
  };

  const getLicenseValue = (client) => {
    // Prefer organization.license_id because backend response includes it there.
    const organizations = getRelatedArray(client, [
      "organization",
      "organizations",
      "client_organizations",
    ]).filter((o) => Number(o?.client_id) === Number(client?.client_id));

    const orgLicenseIds = organizations
      .map((o) => o?.license_id)
      .filter((v) => v !== null && v !== undefined && String(v).trim() !== "");
    if (orgLicenseIds.length) {
      return [...new Set(orgLicenseIds.map((v) => String(v)))].join(", ");
    }

    const licenses = getRelatedArray(client, [
      "client_licenses",
      "licenses",
      "client_license",
    ]);

    const names = licenses
      .map((l) => {
        if (typeof l?.license === "string" && l.license.trim() !== "") {
          return l.license.trim();
        }
        if (l?.license && typeof l.license === "object") {
          return (
            l.license?.license_name ||
            l.license?.name ||
            l.license?.title ||
            null
          );
        }
        return null;
      })
      .filter(
        (v) => v !== null && v !== undefined && String(v).trim() !== ""
      );

    const fallback =
      client?.license_name || client?.license || client?.license_title || null;
    if (names.length) return names.join(", ");
    if (fallback && String(fallback).trim() !== "") return String(fallback);
    return "-";
  };

  const getEmployeesCount = (client) => {
    const orgs = getRelatedArray(client, [
      "organization",
      "organizations",
      "client_organizations",
    ]).filter(
          (o) => Number(o?.client_id) === Number(client?.client_id)
        );
    return orgs.reduce((sum, o) => sum + (Number(o?.entities) || 0), 0);
  };

  const getClientOrganizations = (client) => {
    const filtered = getRelatedArray(client, [
      "organization",
      "organizations",
      "client_organizations",
    ]).filter((o) => Number(o?.client_id) === Number(client?.client_id));

    // Deduplicate accidental repeated organization rows
    const seen = new Set();
    return filtered.filter((o) => {
      const key =
        o?.organization_id ??
        `${Number(o?.client_id)}_${normalizeToken(o?.organization_name)}_${normalizeToken(
          o?.organization_short_name
        )}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const getClientUsers = (client) =>
    getRelatedArray(client, [
      "application_user",
      "application_users",
      "users",
      "client_users",
    ]).filter(
          (u) => Number(u?.client_id) === Number(client?.client_id)
        );

  const normalizeToken = (value) =>
    String(value ?? "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  const buildClientOrganizationParams = useCallback((clientId) => {
    return new URLSearchParams({
      page: "1",
      pageSize: "10",
      sortBy: "organization_id",
      sortOrder: "desc",
      filter_client_id: String(clientId),
      filter_client_id_operator: "equals",
    });
  }, []);

  const navigateToClientOrganizations = useCallback(
    (clientId) => {
      if (!clientId && clientId !== 0) return;
      const params = buildClientOrganizationParams(clientId);
      navigate(`/organization/client/organizations?${params.toString()}`);
    },
    [buildClientOrganizationParams, navigate]
  );


  const displayedClients = useMemo(() => {
    return [...clients].sort((a, b) => {
      const aTime = new Date(a?.created_at || 0).getTime();
      const bTime = new Date(b?.created_at || 0).getTime();
      // newest on top, oldest at bottom
      return bTime - aTime;
    });
  }, [clients]);

  const tableRows = useMemo(
    () =>
      displayedClients.map((client) => {
        const primaryUser = getPrimaryUser(client);
        const email = primaryUser?.email
          ? String(primaryUser.email)
          : client?.email || "-";
        const phone = primaryUser
          ? formatPhone(primaryUser)
          : client?.phone
            ? String(client.phone)
            : client?.phone_number
              ? `${client?.country_phone_code || ""} ${client.phone_number}`.trim()
              : "-";
        const createdDate =
          formatDate(
            client?.created_at ||
              client?.client_created_at ||
              client?.createdAt
          ) || "-";
        const orgs = getClientOrganizations(client);
        const users = getClientUsers(client);
        const clientId =
          client?.client_id ??
          client?.organization_client_id ??
          client?.id;
        return {
          ...client,
          id: clientId,
          client_id: clientId,
          email,
          phone,
          created_date: createdDate,
          license_id: getLicenseValue(client),
          organizations_count:
            orgs.length || Number(client?.organizations_count) || 0,
          users_count: users.length || Number(client?.users_count) || 0,
          employees_count: getEmployeesCount(client),
        };
      }),
    [displayedClients]
  );


  // Initialize URL parameters
  useEffect(() => {
    if (hasInitializedURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  // load data
  useEffect(() => {
    if (!hasInitializedURL.current) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();

    setLoading(true);

    const fetchData = async () => {
      try {
        const search = searchParams.get("search");
        const page = Number.parseInt(searchParams.get("page"), 10) || 1;
        const pageSize = Number.parseInt(searchParams.get("pageSize"), 10) || 10;

        const fetchClientsFallback = async () => {
          const qs = new URLSearchParams();
          qs.set("per_page", String(pageSize));
          qs.set("page", String(page));
          if (search && search.trim() !== "") qs.set("search", search.trim());

          const res = await fetch(
            `${MAIN_URL}/api/client-management/clients?${qs.toString()}`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
                "Content-Type": "application/json",
              },
              signal: fetchControllerRef.current.signal,
            }
          );

          if (res.status === 401) {
            toast.error("Session Expired!");
            window.location.href = "/login";
            return { data: [], pagination: DEFAULT_PAGINATION };
          }

          if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`);
          }

          const json = await res.json();
          const payload = json?.data;
          const list = Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload)
              ? payload
              : [];

          // Laravel paginator shape (if present): { current_page, per_page, total, last_page, ... }
          const total =
            payload?.total ?? (Array.isArray(list) ? list.length : 0);
          const totalPages = payload?.last_page ?? 1;
          const currentPage = payload?.current_page ?? page;
          const perPage = payload?.per_page ?? pageSize;

          return {
            data: list,
            pagination: {
              page: currentPage,
              pageSize: perPage,
              total,
              totalPages,
              hasPrev: currentPage > 1,
              hasNext: currentPage < totalPages,
            },
          };
        };

        // Primary API: clients list, then enrich per client_id
        const response = await fetch(
          `${MAIN_URL}/api/client-management/clients?per_page=${pageSize}&page=${page}${search && search.trim() !== "" ? `&search=${encodeURIComponent(search.trim())}` : ""}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            signal: fetchControllerRef.current.signal,
          }
        );

        if (response.status === 401) {
          toast.error("Session Expired!");
          window.location.href = "/login";
          return;
        }

        let data;
        if (response.ok) {
          const result = await response.json();
          const payload = result?.data ?? result;
          const list = Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload)
              ? payload
              : [];

          const total =
            payload?.total ?? (Array.isArray(list) ? list.length : 0);
          const totalPages = payload?.last_page ?? 1;
          const currentPage = payload?.current_page ?? page;
          const perPage = payload?.per_page ?? pageSize;

          setPaginationData({
            page: currentPage,
            pageSize: perPage,
            total: Number(total) || 0,
            totalPages: Number(totalPages) || 1,
            hasNext: currentPage < totalPages,
            hasPrev: currentPage > 1,
          });

          // Enrich each client with users + organizations using required client_id
          data = await Promise.all(
            list.map(async (client) => {
              const clientId = client?.client_id ?? client?.id;
              if (!clientId) return client;

              try {
                const detailRes = await fetch(
                  `${MAIN_URL}/api/client-management/clients/with-users-organizations?client_id=${encodeURIComponent(clientId)}`,
                  {
                    method: "GET",
                    headers: {
                      Authorization: `Bearer ${localStorage.getItem("token")}`,
                      "Content-Type": "application/json",
                    },
                    signal: fetchControllerRef.current.signal,
                  }
                );

                if (!detailRes.ok) return client;
                const detailJson = await detailRes.json();

                return {
                  ...client,
                  application_user:
                    detailJson?.application_users ??
                    client?.application_user ??
                    client?.application_users ??
                    [],
                  organization:
                    detailJson?.organizations ??
                    client?.organization ??
                    client?.organizations ??
                    [],
                };
              } catch {
                return client;
              }
            })
          );
        } else if (response.status === 404) {
          // Fallback to old GET endpoint if this route is unavailable
          const fallback = await fetchClientsFallback();
          data = fallback.data || [];
          setPaginationData(fallback.pagination || DEFAULT_PAGINATION);
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Filter out items that are actually unassigned (unassigned_on is not "-", null, undefined, or empty)
        let filteredClients = data.filter((item) => {
          const unassignedOn = item.unassigned_on;
          // Keep items where unassigned_on is "-", null, undefined, or empty string (meaning still assigned)
          return !unassignedOn || unassignedOn === "-" || unassignedOn === "";
        });
        
        let clients = filteredClients.map((item) => ({
          ...item,
          id: item.client_id ?? item.id,
          client_name: item.client_name ?? "-",
        }));
        setClients(clients);
      } catch (err) {
        if (err.name === "AbortError") return;
        
        console.error("Failed to fetch clients:", err);
        toast.error("Failed to fetch clients");
        setClients([]);
        setPaginationData(DEFAULT_PAGINATION);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [searchParams]);

  const resolveClientId = (item) => {
    if (item == null) return null;
    if (typeof item !== "object") return item;
    return (
      item.client_id ?? item.organization_client_id ?? item.id
    );
  };

  const handleEdit = useCallback(
    (item) => {
      const id = resolveClientId(item);
      if (id == null) return;
      navigate(`/organization/client/edit/${id}`);
    },
    [navigate]
  );

  const handleShow = useCallback(
    (item) => {
      const id = resolveClientId(item);
      if (id == null) return;
      navigate(`/organization/client/view/${id}`);
    },
    [navigate]
  );

  const handleSortChange = useCallback(
    (field, direction) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("sortBy", field);
        next.set("sortOrder", direction);
        next.set("page", "1");
        return next;
      });
    },
    [setSearchParams]
  );

  const renderClientCell = useCallback(
    (row, col) => {
      if (col?.key === "organizations_count") {
        return (
          <Box
            component="span"
            sx={{
              cursor: "pointer",
              textDecoration: "underline",
              fontWeight: 700,
            }}
            onClick={(e) => {
              e.stopPropagation();
              navigateToClientOrganizations(row?.client_id ?? row?.id);
            }}
          >
            {row?.organizations_count ?? 0}
          </Box>
        );
      }
      return undefined;
    },
    [navigateToClientOrganizations]
  );

  const summaryCards = useMemo(
    () => [
      {
        key: "total",
        label: "Total clients",
        value: paginationData?.total ?? tableRows.length,
        iconKey: "users",
      },
      {
        key: "orgs",
        label: "With organizations",
        value: countWhere(tableRows, (row) => Number(row?.organizations_count) > 0),
        iconKey: "building",
        tone: "ok",
      },
      {
        key: "users",
        label: "With users",
        value: countWhere(tableRows, (row) => Number(row?.users_count) > 0),
        iconKey: "userCheck",
      },
      {
        key: "employees",
        label: "With employees",
        value: countWhere(tableRows, (row) => Number(row?.employees_count) > 0),
        iconKey: "hash",
      },
    ],
    [paginationData, tableRows]
  );

  return (
    <>
      <Layout4
        loading={loading}
        heading={"Clients"}
        add_action={"CLIENT_ADD"}
        btnName={"Add Clients"}
        Route={"/organization/client"}
      />

      <ListSummaryCards cards={summaryCards} />

      <TableDataGeneric
        tableName="clients"
        primaryKey="client_id"
        heading="Clients"
        data={tableRows}
        sortname="client_id"
        showActions={true}
        Route="/organization/client"
        EditFunc={handleEdit}
        handleShow={handleShow}
        onSortChange={handleSortChange}
        token={localStorage.getItem("token")}
        configss={DEFAULT_COLUMNS}
        paginationData={paginationData}
        setPaginationData={setPaginationData}
        filterModule="clients"
        renderCell={renderClientCell}
        edit_delete_action={["CLIENT_EDIT", "CLIENT_DELETE"]}
      />
    </>
  );
}

export default ClientList;
